import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Radio,
  Users,
  Smartphone,
  Package,
  Box,
  Heart,
  Activity,
  RefreshCw,
  AlertTriangle,
  Layers,
} from 'lucide-react'
import {
  dashboardBMService,
  type DashboardStats,
  type BrandBreakdownEntry,
} from '../services/dashboardBMService'

type MessageType = 'success' | 'error' | 'warning' | 'info'

export interface BMDashboardProps {
  showMessage: (msg: string, type: MessageType) => void
}

interface ActivityItem {
  id?: string | number
  type?: string
  message?: string
  description?: string
  title?: string
  action?: string
  entity?: string
  timestamp?: string
  created_at?: string
  at?: string
  time?: string
}

function readNestedNumber(root: unknown, path: string[]): number {
  let cur: unknown = root
  for (const key of path) {
    if (cur && typeof cur === 'object' && key in cur) {
      cur = (cur as Record<string, unknown>)[key]
    } else {
      return 0
    }
  }
  return typeof cur === 'number' && !Number.isNaN(cur) ? cur : 0
}

function activityTimestamp(item: ActivityItem): string | undefined {
  return item.timestamp ?? item.created_at ?? item.at ?? item.time
}

function activityText(item: ActivityItem): string {
  if (item.message) return item.message
  if (item.description) return item.description
  if (item.title) return item.title
  if (item.action && item.entity) return `${item.action}: ${item.entity}`
  if (item.action) return item.action
  try {
    return JSON.stringify(item)
  } catch {
    return String(item)
  }
}

// Brand-aware card styling. Stays in sync with the badge colours used in
// BaliseManagement so the dashboard and inventory tabs share the same
// visual language: an operator scanning a long list of cards or rows
// should never have to re-learn what "amber means JIMI" mid-page.
function brandCardStyle(brand: string): {
  bg: string
  iconBg: string
  iconColor: string
  badge: string
} {
  const u = brand.toUpperCase()
  if (u.includes('MAXVISION')) {
    return {
      bg: 'bg-blue-50 border-blue-100',
      iconBg: 'bg-white/70',
      iconColor: 'text-blue-600',
      badge: 'bg-blue-100 text-blue-800 border-blue-200',
    }
  }
  if (u.includes('JOINTECH')) {
    return {
      bg: 'bg-emerald-50 border-emerald-100',
      iconBg: 'bg-white/70',
      iconColor: 'text-emerald-600',
      badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    }
  }
  if (u.includes('TELTONIKA')) {
    return {
      bg: 'bg-violet-50 border-violet-100',
      iconBg: 'bg-white/70',
      iconColor: 'text-violet-600',
      badge: 'bg-violet-100 text-violet-800 border-violet-200',
    }
  }
  if (u.includes('JIMI')) {
    return {
      bg: 'bg-amber-50 border-amber-100',
      iconBg: 'bg-white/70',
      iconColor: 'text-amber-600',
      badge: 'bg-amber-100 text-amber-800 border-amber-200',
    }
  }
  return {
    bg: 'bg-gray-50 border-gray-200',
    iconBg: 'bg-white/70',
    iconColor: 'text-gray-600',
    badge: 'bg-gray-100 text-gray-700 border-gray-200',
  }
}

function activityDotClass(type?: string): string {
  const t = (type ?? '').toLowerCase()
  if (t.includes('error') || t.includes('fail')) return 'bg-red-500'
  if (t.includes('warn')) return 'bg-amber-500'
  if (t.includes('success') || t.includes('ok')) return 'bg-emerald-500'
  if (t.includes('sim')) return 'bg-cyan-500'
  if (t.includes('customer') || t.includes('user')) return 'bg-pink-500'
  if (t.includes('balise') || t.includes('device')) return 'bg-blue-500'
  return 'bg-indigo-500'
}

function formatRelativeTime(
  iso: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (!iso) return t('bmDashboard.timeUnknown', { defaultValue: 'Unknown time' })
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return t('bmDashboard.timeUnknown', { defaultValue: 'Unknown time' })
  const diffMs = Date.now() - d.getTime()
  const sec = Math.floor(diffMs / 1000)
  if (sec < 45) return t('bmDashboard.justNow', { defaultValue: 'Just now' })
  const min = Math.floor(sec / 60)
  if (min < 60)
    return t('bmDashboard.minutesAgo', { count: min, defaultValue: '{{count}} min ago' })
  const hr = Math.floor(min / 60)
  if (hr < 48)
    return t('bmDashboard.hoursAgo', { count: hr, defaultValue: '{{count}} h ago' })
  const day = Math.floor(hr / 24)
  return t('bmDashboard.daysAgo', { count: day, defaultValue: '{{count}} d ago' })
}

const BMDashboard: React.FC<BMDashboardProps> = ({ showMessage }) => {
  const { t } = useTranslation()
  const showMessageRef = useRef(showMessage)
  useEffect(() => {
    showMessageRef.current = showMessage
  }, [showMessage])

  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [brandBreakdown, setBrandBreakdown] = useState<BrandBreakdownEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const fetchAll = useCallback(
    async (isRefresh = false) => {
      setError(null)
      if (isRefresh) setRefreshing(true)
      else setLoading(true)
      try {
        // Brand breakdown is fetched alongside the canonical statistics
        // so the dashboard reflects a single point-in-time view. A failure
        // in one of the parallel brand queries is already absorbed inside
        // dashboardBMService.getBrandBreakdown (it returns 0 rather than
        // throwing) — the dashboard must not blank because a single brand
        // integration is offline.
        const [s, act, brands] = await Promise.all([
          dashboardBMService.getStatistics(),
          dashboardBMService.getRecentActivity(),
          dashboardBMService.getBrandBreakdown(),
        ])
        setStats(s)
        setActivity(Array.isArray(act) ? (act as ActivityItem[]) : [])
        setBrandBreakdown(brands)
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        if (!isRefresh) {
          setError(msg)
          setStats(null)
        }
        showMessageRef.current(
          t('bmDashboard.loadFailed', { defaultValue: 'Could not load dashboard: {{msg}}', msg }),
          'error'
        )
      } finally {
        if (isRefresh) setRefreshing(false)
        else setLoading(false)
      }
    },
    [t]
  )

  useEffect(() => {
    void fetchAll(false)
  }, [fetchAll])

  const onRefresh = () => {
    void fetchAll(true)
  }

  const totalContainers = readNestedNumber(stats, ['containers', 'total'])
  const totalAssets = readNestedNumber(stats, ['assets', 'total'])

  const healthPct =
    stats?.systemHealth?.overallHealthScore != null
      ? Math.round(
          stats.systemHealth.overallHealthScore <= 1
            ? stats.systemHealth.overallHealthScore * 100
            : stats.systemHealth.overallHealthScore
        )
      : 0

  const utilization = stats?.derived?.utilizationRate ?? 0
  const balPerCust = stats?.derived?.balisesPerCustomer ?? 0
  const simUtil = stats?.derived?.simUtilizationRate ?? 0

  const fmtPct = (n: number) =>
    `${(n <= 1 && n > 0 ? n * 100 : n).toFixed(1)}%`

  const lastUpdatedLabel = stats?.lastUpdated
    ? formatRelativeTime(stats.lastUpdated, t)
    : null

  const kpiCards = [
    {
      label: t('bmDashboard.kpiBalises', { defaultValue: 'Balises' }),
      value: stats?.balises?.total_balises ?? 0,
      icon: Radio,
      bg: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      label: t('bmDashboard.kpiCustomers', { defaultValue: 'Customers' }),
      value: stats?.customers?.totalCustomers ?? 0,
      icon: Users,
      bg: 'bg-pink-50',
      iconColor: 'text-pink-600',
    },
    {
      label: t('bmDashboard.kpiSim', { defaultValue: 'SIM cards' }),
      value: stats?.simCards?.total_sim_cards ?? 0,
      icon: Smartphone,
      bg: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
    },
    {
      label: t('bmDashboard.kpiContainers', { defaultValue: 'Containers' }),
      value: totalContainers,
      icon: Package,
      bg: 'bg-purple-50',
      iconColor: 'text-purple-600',
    },
    {
      label: t('bmDashboard.kpiAssets', { defaultValue: 'Assets' }),
      value: totalAssets,
      icon: Box,
      bg: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      label: t('bmDashboard.kpiHealth', { defaultValue: 'Health score' }),
      value: `${healthPct}%`,
      icon: Heart,
      bg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
  ]

  if (loading && !stats && !error) {
    return (
      <div className="h-full bg-gray-50 flex flex-col overflow-hidden items-center justify-center gap-3">
        <RefreshCw className="w-10 h-10 text-blue-500 animate-spin" aria-hidden />
        <p className="text-sm text-gray-600">{t('loading', { defaultValue: 'Loading...' })}</p>
      </div>
    )
  }

  if (error && !stats) {
    return (
      <div className="h-full bg-gray-50 flex flex-col overflow-hidden items-center justify-center p-6">
        <div className="max-w-md bg-white rounded-xl border border-red-200 shadow-sm p-6 text-center space-y-4">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto" aria-hidden />
          <div>
            <h2 className="font-semibold text-gray-900">
              {t('bmDashboard.errorTitle', { defaultValue: 'Dashboard unavailable' })}
            </h2>
            <p className="text-sm text-gray-600 mt-2">{error}</p>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white px-4 py-2 text-sm font-medium hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4" />
            {t('bmDashboard.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="w-5 h-5 text-gray-600 shrink-0" />
          <h1 className="text-lg font-semibold text-gray-900 truncate">
            {t('bmDashboard.title', { defaultValue: 'Balise management overview' })}
          </h1>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {t('bmDashboard.refresh', { defaultValue: 'Refresh' })}
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {kpiCards.map(({ label, value, icon: Icon, bg, iconColor }) => (
            <div key={label} className={`${bg} rounded-xl p-4 border border-black/5 shadow-sm`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
                  <div className="text-xs font-medium text-gray-600 mt-1 uppercase tracking-wide">
                    {label}
                  </div>
                </div>
                <div className={`shrink-0 p-2 rounded-lg bg-white/60 ${iconColor}`}>
                  <Icon className="w-6 h-6" aria-hidden />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Fleet by brand — shows a card per known integration (MaxVision,
            Jointech, JIMI) with the live count of onboarded balises. We
            keep the card visible even when the count is 0 so JIMI (and
            any future brand) stays discoverable. The card is read-only;
            navigating into the brand-specific view happens from the
            Balise Management tabs. */}
        <section className="max-w-5xl mx-auto space-y-2">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 px-1">
            <Layers className="w-4 h-4 text-gray-500" />
            {t('bmDashboard.fleetByBrand', { defaultValue: 'Fleet by brand' })}
          </h2>
          {brandBreakdown.length === 0 ? (
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm text-sm text-gray-500">
              {t('bmDashboard.fleetByBrandEmpty', {
                defaultValue: 'No brand telemetry yet.',
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {brandBreakdown.map(({ brand, label, total }) => {
                const style = brandCardStyle(brand)
                // Every brand card is now a doorway into the unified
                // inventory, filtered by that brand. We dispatch a
                // window-level CustomEvent so BaliseManagement can pick
                // it up and switch to its Inventory tab + apply the
                // brand filter — without coupling this dashboard to it.
                const handleClick = () => {
                  try {
                    window.dispatchEvent(new CustomEvent('fleet:filter-balise-inventory', {
                      detail: { brand },
                    }))
                  } catch {
                    /* SSR / older browser fallback — silently ignore */
                  }
                }
                return (
                  <button
                    key={brand}
                    type="button"
                    onClick={handleClick}
                    className={`${style.bg} rounded-xl p-4 border shadow-sm text-left hover:shadow-md hover:scale-[1.01] transition-all cursor-pointer`}
                    title={
                      t('bmDashboard.brandOpenInventory', {
                        defaultValue: 'Open inventory · {{brand}}',
                        brand: label,
                      }) as string
                    }
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-2xl font-bold text-gray-900 tabular-nums">
                          {total}
                        </div>
                        <div className="text-xs font-medium text-gray-600 mt-1 uppercase tracking-wide">
                          {t('bmDashboard.brandBalises', {
                            defaultValue: '{{brand}} balises',
                            brand: label,
                          })}
                        </div>
                      </div>
                      <div className={`shrink-0 p-2 rounded-lg ${style.iconBg} ${style.iconColor}`}>
                        <Radio className="w-6 h-6" aria-hidden />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${style.badge}`}
                      >
                        {label}
                      </span>
                      {total === 0 && (
                        <span className="text-[11px] text-gray-500">
                          {t('bmDashboard.brandNoneOnboarded', {
                            defaultValue: 'none onboarded yet',
                          })}
                        </span>
                      )}
                      <span className="ml-auto text-[11px] text-blue-700 font-medium">
                        {t('bmDashboard.brandOpenInventoryArrow', {
                          defaultValue: 'Open inventory →',
                        })}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-5xl mx-auto">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-xl font-bold text-gray-900 tabular-nums">{fmtPct(utilization)}</div>
            <div className="text-xs text-gray-500 mt-1">
              {t('bmDashboard.utilizationRate', { defaultValue: 'Utilization rate' })}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-xl font-bold text-gray-900 tabular-nums">
              {balPerCust.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {t('bmDashboard.balisesPerCustomer', { defaultValue: 'Balises per customer' })}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="text-xl font-bold text-gray-900 tabular-nums">{fmtPct(simUtil)}</div>
            <div className="text-xs text-gray-500 mt-1">
              {t('bmDashboard.simUtilization', { defaultValue: 'SIM utilization rate' })}
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-500" />
              <h2 className="text-sm font-semibold text-gray-900">
                {t('bmDashboard.recentActivity', { defaultValue: 'Recent Activity' })}
              </h2>
            </div>
            <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {activity.length === 0 ? (
                <li className="px-4 py-8 text-center text-sm text-gray-500">
                  {t('bmDashboard.noActivity', { defaultValue: 'No recent activity.' })}
                </li>
              ) : (
                activity.map((item, idx) => {
                  const ts = activityTimestamp(item)
                  const key = item.id ?? `${ts ?? 'row'}-${idx}`
                  return (
                    <li key={key} className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50/80">
                      <span
                        className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${activityDotClass(
                          item.type
                        )}`}
                        aria-hidden
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800">{activityText(item)}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {formatRelativeTime(ts, t)}
                        </p>
                      </div>
                    </li>
                  )
                })
              )}
            </ul>
          </div>
        </div>

        {stats?.lastUpdated ? (
          <p className="text-center text-xs text-gray-400 pb-2">
            {t('bmDashboard.lastUpdated', {
              defaultValue: 'Last updated: {{time}}',
              time: lastUpdatedLabel ?? stats.lastUpdated,
            })}
          </p>
        ) : (
          <p className="text-center text-xs text-gray-400 pb-2">
            {t('bmDashboard.lastUpdatedUnknown', { defaultValue: 'Last updated: —' })}
          </p>
        )}
      </div>
    </div>
  )
}

export default BMDashboard
